/**
 * @license Copyright (c) Microsoft Corporation.  All rights reserved.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { FormRender } from "./FormRender";
import { JsonFormSerializationBuilder } from "./JsonFormSerializationBuilder";
import { FormSerializer } from "./FormSerializer";
import { LookupControlFactory } from "./Lookups/LookupControlFactory";
import { FormValidation } from "./Validation/FormValidation";
import { MultiSelectValidator } from "./Validation/MultiSelectValidator";
import { HtmlAttributes } from "../../../Common/Constants/HtmlAttributes";
import { createFormSubmitEvent } from "./Events/CreateFormSubmitEvent";
import { createAfterFormSubmitFailEvent, createAfterFormSubmitSuccessEvent } from "./Events/CreateAfterFormSubmitEvent";
import { createAfterFormLoadEvent } from "./Events/CreateAfterFormLoadEvent";
import { TopicValidator } from "./Validation/TopicValidator";
import { FormSubmitStatus } from "./Constants/FormStatus";
import { ErrorFeedbackMap, ErrorFeedback, ReloadFeedback, createCorsErrorFeedback, showFeedback, showRenderingFeedback, showSubmissionFeedback } from "./Feedback";
import { doesPhoneNumberStartWithCountryCode } from "./Utils/HtmlUtils";
import { SessionsValidator } from "./EventManagement/Validation";
import { EventFormSerializer } from "./EventManagement/FormSerialization";
import { SubmissionFeedbackProvider } from "./Feedback/SubmissionFeedbackProvider";
import { createFormSubmissionCookie } from "./Utils/FormSubmissionCookieUtils";
var submitButtonStateKey = Symbol("d365mktSubmitButtonState");
var FormLoader = /** @class */ (function () {
    function FormLoader(logger, formServiceClient, translate) {
        this.logger = logger;
        this.formServiceClient = formServiceClient;
        this.translate = translate;
    }
    FormLoader.prototype.loadForm = function (form) {
        return __awaiter(this, void 0, void 0, function () {
            var formResponse, e_1, feedback;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (form === null || form === void 0 ? void 0 : form.Error) {
                            showFeedback(__assign(__assign({}, ErrorFeedback), { message: form.Error }), form);
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, 5, 6]);
                        FormRender.loadForm(form);
                        return [4 /*yield*/, this.formServiceClient.getFormByFormUrl(form.FormUrl, form.FormLoadResponseType)];
                    case 2:
                        formResponse = _a.sent();
                        FormRender.renderForm(form, formResponse.formHtml, formResponse.formRenderingStatus);
                        return [4 /*yield*/, this.registerHandlersToForm(form)];
                    case 3:
                        _a.sent();
                        showRenderingFeedback(formResponse.formRenderingStatus, form);
                        // we don't await here intentionally - we don't want to wait until form visit is created.
                        this.sendFormVisitInteraction(form);
                        form.FormPlaceholders.forEach(function (placeholder) { return placeholder.Element.dispatchEvent(createAfterFormLoadEvent()); });
                        return [3 /*break*/, 6];
                    case 4:
                        e_1 = _a.sent();
                        this.logger.error(e_1.message);
                        feedback = __assign(__assign({}, ReloadFeedback), { message: "FormFailedToLoad" });
                        if (e_1.name === "TypeError" && e_1.message === "Failed to fetch") {
                            feedback = createCorsErrorFeedback(form, this.formServiceClient);
                        }
                        showFeedback(feedback, form);
                        return [3 /*break*/, 6];
                    case 5:
                        FormRender.removeLoader(form);
                        return [7 /*endfinally*/];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    FormLoader.prototype.registerHandlersToForm = function (form) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.attachSubmitHandlers(form);
                        return [4 /*yield*/, this.attachValidationHandlers(form)];
                    case 1:
                        _a.sent();
                        this.attachPhoneFieldHandlers(form);
                        this.attachLookupsAutosuggest(form);
                        return [2 /*return*/];
                }
            });
        });
    };
    FormLoader.prototype.sendFormVisitInteraction = function (form) {
        return __awaiter(this, void 0, void 0, function () {
            var url, payload, ex_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (form.DoNotTrack === true) {
                            return [2 /*return*/];
                        }
                        url = window.location.href;
                        payload = { pageUrl: url };
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.formServiceClient.sendFormVisit(form.FormVisitUrl, payload)];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        ex_1 = _a.sent();
                        this.logger.error(ex_1.message);
                        showFeedback(createCorsErrorFeedback(form, this.formServiceClient), form);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    FormLoader.prototype.getFormSubmitProperties = function (form, formElement) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
        return __awaiter(this, void 0, void 0, function () {
            var _t, _u, _v, _w, _x;
            var _y;
            return __generator(this, function (_z) {
                switch (_z.label) {
                    case 0:
                        _y = {};
                        if (!((_c = (_b = (_a = form.FormSubmitProperties) === null || _a === void 0 ? void 0 : _a.SuccessMessage) !== null && _b !== void 0 ? _b : formElement.getAttribute(HtmlAttributes.successMessage)) !== null && _c !== void 0)) return [3 /*break*/, 1];
                        _t = _c;
                        return [3 /*break*/, 3];
                    case 1: return [4 /*yield*/, this.translate("FormSubmitted")];
                    case 2:
                        _t = (_z.sent());
                        _z.label = 3;
                    case 3:
                        _y.SuccessMessage = _t;
                        if (!((_f = (_e = (_d = form.FormSubmitProperties) === null || _d === void 0 ? void 0 : _d.WaitlistMessage) !== null && _e !== void 0 ? _e : formElement.getAttribute(HtmlAttributes.waitlistMessage)) !== null && _f !== void 0)) return [3 /*break*/, 4];
                        _u = _f;
                        return [3 /*break*/, 6];
                    case 4: return [4 /*yield*/, this.translate("FormSubmittedWaitlist")];
                    case 5:
                        _u = (_z.sent());
                        _z.label = 6;
                    case 6:
                        _y.WaitlistMessage = _u;
                        if (!((_j = (_h = (_g = form.FormSubmitProperties) === null || _g === void 0 ? void 0 : _g.ErrorMessage) !== null && _h !== void 0 ? _h : formElement.getAttribute(HtmlAttributes.errorMessage)) !== null && _j !== void 0)) return [3 /*break*/, 7];
                        _v = _j;
                        return [3 /*break*/, 9];
                    case 7: return [4 /*yield*/, this.translate("FormSubmitError")];
                    case 8:
                        _v = (_z.sent());
                        _z.label = 9;
                    case 9:
                        _y.ErrorMessage = _v;
                        if (!((_m = (_l = (_k = form.FormSubmitProperties) === null || _k === void 0 ? void 0 : _k.NotStartedMessage) !== null && _l !== void 0 ? _l : formElement.getAttribute(HtmlAttributes.notStartedMessage)) !== null && _m !== void 0)) return [3 /*break*/, 10];
                        _w = _m;
                        return [3 /*break*/, 12];
                    case 10: return [4 /*yield*/, this.translate("FormRegistrationNotStarted")];
                    case 11:
                        _w = (_z.sent());
                        _z.label = 12;
                    case 12:
                        _y.NotStartedMessage = _w;
                        if (!((_q = (_p = (_o = form.FormSubmitProperties) === null || _o === void 0 ? void 0 : _o.ClosedMessage) !== null && _p !== void 0 ? _p : formElement.getAttribute(HtmlAttributes.closedMessage)) !== null && _q !== void 0)) return [3 /*break*/, 13];
                        _x = _q;
                        return [3 /*break*/, 15];
                    case 13: return [4 /*yield*/, this.translate("FormRegistrationClosed")];
                    case 14:
                        _x = (_z.sent());
                        _z.label = 15;
                    case 15: return [2 /*return*/, (_y.ClosedMessage = _x,
                            _y.RedirectUrl = (_s = (_r = form.FormSubmitProperties) === null || _r === void 0 ? void 0 : _r.RedirectUrl) !== null && _s !== void 0 ? _s : formElement.getAttribute(HtmlAttributes.redirectUrl),
                            _y.PreventSubmissionUi = form.FormSubmitProperties.PreventSubmissionUi || formElement.getAttribute(HtmlAttributes.preventSubmissionUi) === "true",
                            _y)];
                }
            });
        });
    };
    FormLoader.prototype.attachSubmitHandlers = function (form) {
        var _this = this;
        form.FormPlaceholders.forEach(function (placeholder) {
            var allForms = placeholder.Element.querySelectorAll("form");
            var _loop_1 = function (i) {
                var currentForm = allForms[i];
                currentForm.addEventListener("submit", function (e) { return __awaiter(_this, void 0, void 0, function () {
                    return __generator(this, function (_a) {
                        switch (_a.label) {
                            case 0: return [4 /*yield*/, this.handleFormSubmission(e, form, currentForm, placeholder)
                                    .then(function (status) { return status && showSubmissionFeedback(status, currentForm); })];
                            case 1: return [2 /*return*/, _a.sent()];
                        }
                    });
                }); });
            };
            for (var i = 0; i < allForms.length; i++) {
                _loop_1(i);
            }
        });
    };
    FormLoader.prototype.handleFormSubmission = function (event, form, currentForm, placeholder) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var tokenField, token, _b, _c, formSubmitProps, hasRedirectUrl;
            var _this = this;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        event.preventDefault();
                        if (!this.isReCaptchaV2Used(currentForm)) return [3 /*break*/, 3];
                        tokenField = currentForm.querySelector("." + HtmlAttributes.gRecaptchaResponse);
                        token = (_a = tokenField === null || tokenField === void 0 ? void 0 : tokenField.value) !== null && _a !== void 0 ? _a : "";
                        if (!!token) return [3 /*break*/, 2];
                        _b = this.showRecaptchaError;
                        _c = [currentForm];
                        return [4 /*yield*/, this.translate("ReCaptchaNotVerifiedMessage")];
                    case 1:
                        _b.apply(this, _c.concat([_d.sent()]));
                        return [2 /*return*/, null];
                    case 2:
                        this.showRecaptchaError(currentForm, "");
                        _d.label = 3;
                    case 3: return [4 /*yield*/, this.getFormSubmitProperties(form, currentForm)];
                    case 4:
                        formSubmitProps = _d.sent();
                        hasRedirectUrl = !!formSubmitProps.RedirectUrl;
                        return [4 /*yield*/, this.withDisabledSubmitButtons(currentForm, hasRedirectUrl, function () { return __awaiter(_this, void 0, void 0, function () {
                                var payloadObject, payload, cancelled, response, _a;
                                return __generator(this, function (_b) {
                                    switch (_b.label) {
                                        case 0: return [4 /*yield*/, this.validateLookups(currentForm)];
                                        case 1:
                                            if (!(_b.sent())) {
                                                return [2 /*return*/, null];
                                            }
                                            payloadObject = null;
                                            payload = this.serializeForm(form, currentForm);
                                            if ((payload === null || payload === void 0 ? void 0 : payload.data) && payload.data.length) {
                                                payloadObject = JSON.parse(payload === null || payload === void 0 ? void 0 : payload.data);
                                            }
                                            cancelled = !currentForm.dispatchEvent(createFormSubmitEvent(payloadObject));
                                            if (cancelled) {
                                                return [2 /*return*/, null];
                                            }
                                            if (!this.validateDates(currentForm)) {
                                                return [2 /*return*/, null];
                                            }
                                            _b.label = 2;
                                        case 2:
                                            _b.trys.push([2, 5, , 6]);
                                            return [4 /*yield*/, this.formServiceClient.submitForm(form.SubmitFormUrl, payload.data)];
                                        case 3:
                                            response = _b.sent();
                                            return [4 /*yield*/, this.handleFormSubmissionResponse(response, form, currentForm, payloadObject, formSubmitProps)];
                                        case 4: return [2 /*return*/, _b.sent()];
                                        case 5:
                                            _a = _b.sent();
                                            currentForm.dispatchEvent(createAfterFormSubmitFailEvent(payloadObject));
                                            return [2 /*return*/, formSubmitProps.PreventSubmissionUi ? null : __assign(__assign({}, ErrorFeedback), { message: formSubmitProps.ErrorMessage || ErrorFeedback.message })];
                                        case 6: return [2 /*return*/];
                                    }
                                });
                            }); })];
                    case 5: return [2 /*return*/, _d.sent()];
                }
            });
        });
    };
    FormLoader.prototype.showRecaptchaError = function (form, text) {
        var errorMessage = form.querySelector("." + HtmlAttributes.reCaptchaErrorText);
        if (!errorMessage) {
            return;
        }
        errorMessage.textContent = text;
        if (text) {
            errorMessage.setAttribute("aria-live", "assertive");
        }
        else {
            errorMessage.removeAttribute("aria-live");
        }
    };
    FormLoader.prototype.isReCaptchaV2Used = function (form) {
        return !!form.querySelector("#" + HtmlAttributes.reCaptchaV2SiteKeyHolder);
    };
    FormLoader.prototype.validateLookups = function (currentForm) {
        return __awaiter(this, void 0, void 0, function () {
            var lookups;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        lookups = Array.from(currentForm.elements || [])
                            .map(function (e) { return LookupControlFactory.getLookupFieldControlsFromInput(e); })
                            .filter(function (l) { return !!l; });
                        return [4 /*yield*/, Promise.all(lookups.map(function (control) { return control.syncSelection(); }))];
                    case 1:
                        _a.sent();
                        if (lookups.length == 0 || currentForm.noValidate) {
                            return [2 /*return*/, true];
                        }
                        return [2 /*return*/, currentForm.reportValidity()];
                }
            });
        });
    };
    FormLoader.prototype.validateDates = function (currentForm) {
        var e_2, _a;
        var selected = currentForm.querySelectorAll('input[type=date]');
        if (currentForm.noValidate || !(selected === null || selected === void 0 ? void 0 : selected.length)) {
            return true;
        }
        var marker = "data-d365mkt-invalid";
        var dates = Array.from(selected);
        var customValid = true;
        var _loop_2 = function (field) {
            // preserve external custom validation
            if (field.validity.customError
                && !field.getAttribute(marker)) {
                return "continue";
            }
            field.setCustomValidity("");
            field.removeAttribute(marker);
            // No need to validate if the field is marked as invalid or shouldn't be validated
            if (!field.willValidate
                || !field.checkValidity()) {
                return "continue";
            }
            var val = new Date(field.value);
            if (Number.isNaN(val.getTime())) {
                return "continue";
            }
            var fmt = new Intl.DateTimeFormat(undefined, { dateStyle: 'short' });
            var message = "";
            var max = new Date(field.max);
            if (!Number.isNaN(max.getTime())
                && val.getTime() > max.getTime()) {
                message = "value must be ".concat(fmt.format(max), " or earlier");
            }
            var min = new Date(field.min);
            if (!Number.isNaN(min.getTime())
                && val.getTime() < min.getTime()) {
                message = "value must be ".concat(fmt.format(min), " or later");
            }
            if (message) {
                customValid = false;
                field.setCustomValidity(message);
                field.setAttribute(marker, "true");
                field.addEventListener("input", function () {
                    field.setCustomValidity("");
                    field.removeAttribute(marker);
                }, { once: true });
            }
        };
        try {
            for (var dates_1 = __values(dates), dates_1_1 = dates_1.next(); !dates_1_1.done; dates_1_1 = dates_1.next()) {
                var field = dates_1_1.value;
                _loop_2(field);
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (dates_1_1 && !dates_1_1.done && (_a = dates_1.return)) _a.call(dates_1);
            }
            finally { if (e_2) throw e_2.error; }
        }
        return customValid || currentForm.reportValidity();
    };
    FormLoader.prototype.handleFormSubmissionResponse = function (response, form, currentForm, payloadObject, formSubmitProps) {
        return __awaiter(this, void 0, void 0, function () {
            var baseStatus, eventDispatched;
            return __generator(this, function (_a) {
                if (response.submissionStatus != "OK" && response.submissionStatus != FormSubmitStatus.Success) {
                    currentForm.dispatchEvent(createAfterFormSubmitFailEvent(payloadObject));
                    if (formSubmitProps.PreventSubmissionUi) {
                        return [2 /*return*/, null];
                    }
                    baseStatus = ErrorFeedbackMap[response.submissionStatus] || ErrorFeedback;
                    return [2 /*return*/, __assign(__assign({}, baseStatus), { message: response.errorMessage || baseStatus.message })];
                }
                createFormSubmissionCookie(response.formSubmissionId, this.logger);
                eventDispatched = false;
                if (response.redirectUrl) {
                    currentForm.dispatchEvent(createAfterFormSubmitSuccessEvent(payloadObject));
                    eventDispatched = true;
                    if (this.redirectTo(response.redirectUrl)) {
                        return [2 /*return*/];
                    }
                    else {
                        this.restoreSubmitButtons(currentForm);
                    }
                }
                if (formSubmitProps.RedirectUrl) {
                    currentForm.dispatchEvent(createAfterFormSubmitSuccessEvent(payloadObject));
                    eventDispatched = true;
                    if (this.redirectTo(formSubmitProps.RedirectUrl)) {
                        return [2 /*return*/];
                    }
                    else {
                        this.restoreSubmitButtons(currentForm);
                    }
                }
                if (!eventDispatched) {
                    currentForm.dispatchEvent(createAfterFormSubmitSuccessEvent(payloadObject));
                }
                return [2 /*return*/, formSubmitProps.PreventSubmissionUi ? null : SubmissionFeedbackProvider.getSuccessFeedback(form, currentForm, formSubmitProps)];
            });
        });
    };
    FormLoader.prototype.redirectTo = function (redirectUrl) {
        // when iframe hosting is implemented, must use window.parent.location.href for that case
        try {
            window.location.href = redirectUrl;
            return true;
        }
        catch (e) {
            console.error("Form redirect issue", e);
            return false;
        }
    };
    FormLoader.prototype.attachValidationHandlers = function (form) {
        return __awaiter(this, void 0, void 0, function () {
            var validationConfig;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = {};
                        return [4 /*yield*/, this.translate("ValidationRequiredField")];
                    case 1:
                        _a.fieldRequiredErrorMessage = _b.sent();
                        return [4 /*yield*/, this.translate("ValidationSessionSelectionRequired")];
                    case 2:
                        _a.sessionRegistrationRequiredErrorMessage = _b.sent();
                        return [4 /*yield*/, this.translate("ValidationSingleSessionSelectionRequired")];
                    case 3:
                        validationConfig = (_a.singleSessionRegistrationRequiredErrorMessage = _b.sent(),
                            _a);
                        form.FormPlaceholders.forEach(function (placeholder) {
                            var allForms = placeholder.Element.querySelectorAll("FORM");
                            for (var i = 0; i < allForms.length; i++) {
                                var currentForm = allForms[i];
                                FormLoader.validation.attachValidation(currentForm, validationConfig);
                            }
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    FormLoader.prototype.attachPhoneFieldHandlers = function (form) {
        var _this = this;
        form.FormPlaceholders.forEach(function (placeholder) {
            var phoneFormFieldBlocks = placeholder.Element.querySelectorAll("DIV[".concat(HtmlAttributes.blockType, "=\"PhoneFormField\"]"));
            var _loop_3 = function (i) {
                var input = phoneFormFieldBlocks[i].querySelector('INPUT[type="tel"]');
                if (input) {
                    // Init hide country code action if value was prefilled
                    _this.hidePhoneCountryCodeField(phoneFormFieldBlocks[i], input.value);
                    input.addEventListener("input", function () {
                        _this.hidePhoneCountryCodeField(phoneFormFieldBlocks[i], input.value);
                    });
                }
            };
            for (var i = 0; i < phoneFormFieldBlocks.length; i++) {
                _loop_3(i);
            }
        });
    };
    FormLoader.prototype.hidePhoneCountryCodeField = function (phoneFormFieldBlock, inputValue) {
        var span = phoneFormFieldBlock.querySelector(".phoneCountryCode span");
        var countryCode = phoneFormFieldBlock.getAttribute(HtmlAttributes.dataCountryCode);
        if (!span) {
            return;
        }
        if (!countryCode || countryCode === "+" || doesPhoneNumberStartWithCountryCode(inputValue)) {
            span.hidden = true;
        }
        else {
            span.hidden = false;
        }
    };
    FormLoader.prototype.serializeForm = function (form, formElement) {
        var serializer = new JsonFormSerializationBuilder(window.location.href);
        FormSerializer.serialize(formElement, serializer);
        EventFormSerializer.serialize(form, formElement, serializer);
        return serializer.build();
    };
    FormLoader.prototype.withDisabledSubmitButtons = function (form, hasRedirectUrl, callback) {
        return __awaiter(this, void 0, void 0, function () {
            var submittingMessage;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.translate("FormSubmitting")];
                    case 1:
                        submittingMessage = _a.sent();
                        this.disableSubmitButtons(form, submittingMessage);
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, , 4, 5]);
                        return [4 /*yield*/, callback()];
                    case 3: return [2 /*return*/, _a.sent()];
                    case 4:
                        if (!hasRedirectUrl) {
                            this.restoreSubmitButtons(form);
                        }
                        else {
                            setTimeout(function () {
                                _this.restoreSubmitButtons(form);
                            }, 5000);
                        }
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    FormLoader.prototype.disableSubmitButtons = function (form, submittingMessage) {
        form.querySelectorAll("[type='submit']").forEach(function (button) {
            if (!button[submitButtonStateKey]) {
                button[submitButtonStateKey] = {
                    innerHTML: button.innerHTML,
                    ariaLabel: button.getAttribute("aria-label"),
                    ariaBusy: button.getAttribute("aria-busy"),
                    disabled: button.disabled
                };
            }
            button.setAttribute("aria-label", submittingMessage);
            button.setAttribute("aria-busy", "true");
            button.innerHTML = '<div class="submitButtonSpinner" aria-hidden="true"></div>';
            button.disabled = true;
        });
    };
    FormLoader.prototype.restoreSubmitButtons = function (form) {
        var _this = this;
        form.querySelectorAll("[type='submit']").forEach(function (button) {
            var savedState = button[submitButtonStateKey];
            if (!savedState) {
                return;
            }
            button.innerHTML = savedState.innerHTML;
            _this.restoreAttribute(button, "aria-label", savedState.ariaLabel);
            _this.restoreAttribute(button, "aria-busy", savedState.ariaBusy);
            button.disabled = savedState.disabled;
            delete button[submitButtonStateKey];
        });
    };
    FormLoader.prototype.restoreAttribute = function (element, name, value) {
        if (value === null) {
            element.removeAttribute(name);
        }
        else {
            element.setAttribute(name, value);
        }
    };
    FormLoader.prototype.attachLookupsAutosuggest = function (form) {
        var _this = this;
        form.FormPlaceholders.forEach(function (placeholder) {
            var allLookups = placeholder.Element.querySelectorAll("INPUT[list]");
            for (var i = 0; i < allLookups.length; i++) {
                var element = allLookups[i];
                LookupControlFactory.CreateLookupControl(form, element, _this.logger);
            }
        });
    };
    FormLoader.validation = new FormValidation([new MultiSelectValidator(), new TopicValidator(), new SessionsValidator()]);
    return FormLoader;
}());
export { FormLoader };
